import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Eye, Building2, Clock, User, Shield, RefreshCw } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function SuperAdminImpersonation() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  // eslint-disable-next-line no-unused-vars
  const token = localStorage.getItem('superAdminToken');
  const getHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('superAdminToken')}` });

  const fetchLogs = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/platform/impersonation/logs`, { headers: getHeaders() });
      setLogs(res.data);
    } catch { toast.error('Failed to load impersonation logs'); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const logImpersonation = async (tenantId) => {
    try {
      await axios.post(`${API}/platform/impersonation/log?tenant_id=${tenantId}`, {}, { headers: getHeaders() });
      toast.success('Impersonation session logged');
      fetchLogs();
    } catch { toast.error('Failed to log impersonation'); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-violet-500" /></div>;

  return (
    <div className="space-y-6 max-w-5xl" data-testid="impersonation-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-extrabold text-white tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>Impersonation Audit Log</h1>
          <p className="text-slate-400 mt-1">Track when super admins access tenant accounts</p>
        </div>
        <Button onClick={fetchLogs} variant="outline" className="border-slate-600 text-slate-300 hover:text-white" data-testid="refresh-logs-btn">
          <RefreshCw size={14} className="mr-2" />Refresh
        </Button>
      </div>

      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader className="pb-3">
          <CardTitle className="text-white flex items-center gap-2"><Eye size={18} />Session History</CardTitle>
          <CardDescription className="text-slate-400">All tenant impersonation sessions are logged for compliance and auditing</CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow className="border-slate-700">
                <TableHead className="text-slate-400 font-bold">Admin</TableHead>
                <TableHead className="text-slate-400 font-bold">Tenant Accessed</TableHead>
                <TableHead className="text-slate-400 font-bold">Action</TableHead>
                <TableHead className="text-slate-400 font-bold">Timestamp</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {logs.length > 0 ? logs.map(log => (
                <TableRow key={log.id} className="border-slate-700 hover:bg-slate-800/50" data-testid={`log-${log.id}`}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Shield size={14} className="text-violet-400" />
                      <div>
                        <p className="text-sm font-semibold text-white">{log.admin_name}</p>
                        <p className="text-xs text-slate-500">{log.admin_email}</p>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Building2 size={14} className="text-blue-400" />
                      <div>
                        <p className="text-sm text-white">{log.tenant_name}</p>
                        <p className="text-xs text-slate-500">{log.tenant_slug}</p>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell><span className="text-xs bg-amber-500/20 text-amber-400 border border-amber-500/30 px-2 py-0.5 rounded">Impersonation</span></TableCell>
                  <TableCell className="text-slate-400 text-xs">{log.timestamp?.replace('T', ' ').slice(0, 19)} UTC</TableCell>
                </TableRow>
              )) : (
                <TableRow><TableCell colSpan={4} className="text-center text-slate-500 py-8">No impersonation sessions recorded yet</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
